import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import {
  type Appearance,
  type ResolvedAppearance,
  appearanceStorageKey,
  readAppearance,
  resolveAppearance,
} from "./preferences";

const appearanceStore = createStore(() => ({
  userId: null as string | null,
  appearance: "system" as Appearance,
  resolved: "light" as ResolvedAppearance,
}));

const sessionPreferences = new Map<string, Appearance>();

function applyAppearance(appearance: Appearance) {
  const resolved = resolveAppearance(
    appearance,
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
  appearanceStore.setState({ appearance, resolved });
}

export function setAppearanceUser(userId: string | null) {
  let appearance: Appearance = "system";
  if (userId) {
    try {
      appearance = sessionPreferences.get(userId) ?? readAppearance(window.localStorage, userId);
    } catch {
      appearance = sessionPreferences.get(userId) ?? "system";
    }
  }
  appearanceStore.setState({ userId });
  applyAppearance(appearance);
}

export function setAppearance(appearance: Appearance) {
  const { userId } = appearanceStore.getState();
  if (!userId) {
    return;
  }
  sessionPreferences.set(userId, appearance);
  applyAppearance(appearance);
  try {
    window.localStorage.setItem(appearanceStorageKey(userId), appearance);
    return true;
  } catch {
    return false;
  }
}

export function initializeAppearance(userId: string | null) {
  setAppearanceUser(userId);
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = () => applyAppearance(appearanceStore.getState().appearance);
  const onStorage = (event: StorageEvent) => {
    const { userId: currentUserId } = appearanceStore.getState();
    if (
      currentUserId &&
      (event.key === null || event.key === appearanceStorageKey(currentUserId))
    ) {
      sessionPreferences.delete(currentUserId);
      setAppearanceUser(currentUserId);
    }
  };
  media.addEventListener("change", onSystemChange);
  window.addEventListener("storage", onStorage);
  return () => {
    media.removeEventListener("change", onSystemChange);
    window.removeEventListener("storage", onStorage);
  };
}

export function useAppearance() {
  return useStore(appearanceStore);
}
